import { CLink } from '@/CLink';
import { PrimaryBtn, SecondaryBtn } from '@/buttons';
import { getNavLink } from '@/layout/NavLinks';
import { lgTxt } from '@/layout/styles/Typography';
import { flexCenter, px, py } from '@/layout/styles/classes';
import styled from '@emotion/styled';
import { StaticImage } from 'gatsby-plugin-image';

const Section = styled.section`
    ${py};
    ${px};
    background-color: ${({ theme }) => theme.colors.blue1};

    @media (min-width: 1280px) {
        padding-top: 128px;
        padding-bottom: 128px;
        border-top-left-radius: 256px;
    }
`;

const Flex = styled.div`
    :not(:last-of-type) {
        margin-bottom: 32px;
    }

    @media (min-width: 1024px) {
        ${flexCenter};

        :first-of-type {
            > div:last-of-type {
                max-width: 531px;
            }
        }

        :not(:last-of-type) {
            margin-bottom: 64px;
        }
    }
`;

const Images = styled.div`
    display: grid;
    grid-template-columns: 42.38% 1fr;
    grid-template-rows: 18.05% 36.11% 1fr;
    gap: 8px;
    max-width: 531px;
    aspect-ratio: 361 / 443;

    ::before {
        content: '';
        width: 40%;
        max-width: 96px;
        max-height: 96px;
        height: 100%;
        border-radius: 50%;
        background-color: ${({ theme }) => theme.colors.yellow3};
        grid-area: 1 / 2 / 2 / 3;
        justify-self: start;
        align-self: end;
    }

    ::after {
        content: '';
        max-height: 96px;
        max-width: 128px;
        width: 53.5%;
        height: 100%;
        background-color: ${({ theme }) => theme.colors.blue4};
        border-radius: 64px 0;
        grid-area: 1 / 2 / 2 / 3;
        justify-self: end;
        align-self: end;
    }

    > .gatsby-image-wrapper {
        :first-of-type {
            border-radius: 0px 150px;
            grid-area: 1 / 1 / 4 / 2;
            opacity: 0.9999; //safari overflow bug fix
        }

        :nth-of-type(2) {
            border-radius: 0px 150px 150px 0px;
            grid-area: 2 / 2 / 2 / 3;
            justify-self: start;
            opacity: 0.9999; //safari overflow bug fix
            width: 84%;
        }

        :last-of-type {
            border-radius: 0px 150px 150px 0px;
            grid-area: 3 / 2 / 4 / 3;
            opacity: 0.9999; //safari overflow bug fix
        }
    }

    @media (min-width: 500px) {
        gap: 16px;

        ::after {
            margin-left: 112px;
            justify-self: start;
        }
    }

    @media (min-width: 1024px) {
        max-width: 717px;
        min-width: 531px;

        > .gatsby-image-wrapper:nth-of-type(2) {
            width: 80.67%;
        }
    }

    @media (min-width: 1280px) {
        gap: 32px;
        aspect-ratio: unset;
        grid-template-columns: 54.39% 1fr;
        grid-template-rows: 15.73% 36.88% 1fr;
        flex-shrink: 0;
    }
`;

const TextBox = styled.div<{ desktopLeft?: boolean }>`
    max-width: 531px;
    margin-top: 24px;

    > ul {
        color: ${({ theme }) => theme.colors.gray3};
        margin: 0 0 30px;
    }

    > p {
        margin: 0 0 30px;

        :not(.tagline) {
            color: ${({ theme }) => theme.colors.gray3};
        }

        &.tagline {
            margin: 0;
        }

        &.impTxt {
            ${lgTxt};
            color: ${({ theme }) => theme.colors.gray5};
        }
    }

    > h2 {
        color: ${({ theme }) => theme.colors.blue7};
        margin: 12px 0 30px;
    }

    @media (min-width: 1024px) {
        margin-top: 0;
        margin-right: ${({ theme, desktopLeft }) => (desktopLeft ? theme.spacing.lg : 0)};
        margin-left: ${({ theme, desktopLeft }) => (desktopLeft ? 0 : theme.spacing.lg)};
        order: ${({ desktopLeft }) => (desktopLeft ? -1 : 'unset')};
        max-width: 624px;
        width: 624px;
        flex: 1;
    }
`;

const DifferenceWrapper = styled.div`
    position: relative;
    max-width: 531px;

    ::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 35.45%;
        max-width: 219px;
        height: 32.56vw;
        max-height: 219px;
        background-color: ${({ theme }) => theme.colors.blue7};
        border-radius: 50%;
    }

    > .gatsby-image-wrapper {
        border-radius: 256px 12px;
        height: 608px;
        opacity: 0.9999; //safari overflow bug fix
    }

    @media (min-width: 1024px) {
        max-width: 623px;
        flex-shrink: 0.2;

        ::before {
            height: 17vw;
        }
    }

    @media (min-width: 1280px) {
        flex-shrink: 0;
    }
`;

const BottleWrapper = styled.div`
    max-width: max-content;
    position: relative;
    padding: 0 52.5px;

    ::before {
        ${flexCenter};
        content: 'new';
        width: 108px;
        height: 80px;
        font-size: 34px;
        font-weight: 600;
        line-height: 40px;
        color: ${({ theme }) => theme.colors.white};
        position: absolute;
        top: 0;
        right: 0;
        z-index: 2;
        border-radius: 32px 0px;
        background-color: ${({ theme }) => theme.colors.yellow4};
    }

    > .gatsby-image-wrapper {
        border-radius: 50%;
        opacity: 0.9999; //safari overflow bug fix
        background-color: ${({ theme }) => theme.colors.blue3};
    }

    > div:last-of-type {
        background-color: ${({ theme }) => theme.colors.white};
        border-radius: 0px 32px 32px 32px;
        padding: 16px;
        max-width: max-content;
        margin: -16px auto 0;
        z-index: 2;
        position: relative;

        > p {
            font-weight: 500;
            margin: 0;

            :first-of-type {
                color: ${({ theme }) => theme.colors.blue4};
            }

            :last-of-type {
                color: ${({ theme }) => theme.colors.blue7};
                font-size: 1.25rem;
                line-height: 140%;
                text-align: center;
            }
        }
    }

    @media (min-width: 500px) {
        padding: 0;

        ::before {
            border-radius: 45px 0px;
        }
    }

    @media (min-width: 1280px) {
        display: grid;
        width: 570px;
        max-width: 570px;
        flex: 1;

        > .gatsby-image-wrapper {
            grid-area: 1/1;
            justify-self: start;
        }

        ::before {
            right: unset;
            left: 357px;
        }

        > div:last-of-type {
            border-radius: 0px 60px 60px 60px;
            padding: 24px 32px;
            margin: 0;
            place-self: end;
            grid-area: 1/1;

            > p {
                font-weight: 600;

                :first-of-type {
                    font-size: 1.25rem;
                }

                :last-of-type {
                    font-weight: 600;
                    font-size: 3rem;
                    line-height: 108%;
                    margin: 5px 0 0;
                    text-align: left;
                }
            }
        }
    }
`;

export const Supercharge = () => {
    return (
        <Section>
            <Flex>
                <Images>
                    <StaticImage
                        src="../../../assets/images/home/supercharge/supercharge.jpg"
                        alt="happy girl with hands in the air"
                        width={390}
                        height={610}
                    />
                    <StaticImage
                        src="../../../assets/images/home/supercharge/woman.jpg"
                        alt="woman laughing"
                        width={238}
                        height={225}
                    />
                    <StaticImage
                        src="../../../assets/images/home/supercharge/orange.jpg"
                        alt="girl holding oranges infront of her eyes"
                        width={295}
                        height={225}
                    />
                </Images>
                <TextBox>
                    <p className="tagline">ABOUT az minerals</p>
                    <h2>
                        Supercharge
                        <br />
                        Your Health
                    </h2>
                    <p className="impTxt">
                        We offer choice, reliability and value <br />
                        That’s what makes us better
                    </p>
                    <PrimaryBtn as={CLink} to={getNavLink('our story')}>
                        About us
                    </PrimaryBtn>
                </TextBox>
            </Flex>
            <Flex>
                <DifferenceWrapper>
                    <StaticImage
                        src="../../../assets/images/home/supercharge/difference.jpg"
                        alt="a meal on a table"
                        width={623}
                        height={608}
                    />
                </DifferenceWrapper>

                <TextBox desktopLeft>
                    <p className="tagline">Special service</p>
                    <h2>Feel and See the Difference</h2>
                    <ul>
                        <li>
                            Smart A-Z Minerals are essential for the maintenance of good health.
                        </li>
                        <li>
                            Today’s soils are over-farmed and foods are over-processed, therefore we
                            are not getting needed daily minerals. We want you to feel and see the
                            difference our high quality minerals make. All Smart A-Z Minerals are
                            ionic for maintaining normal absorption and have no preservatives or
                            artificial ingredients.
                        </li>
                        <li>
                            Give your body the boost it needs with Smart A-Z Minerals and discover
                            why they are essential for the maintenance of good health.
                        </li>
                    </ul>
                    <p className="impTxt">
                        These statements have not been evaluated by the Food and Drug
                        Administration. This product is not intended to diagnose, treat, cure, or
                        prevent any disease.
                    </p>
                    <PrimaryBtn as={CLink} to={getNavLink('our process')}>
                        our process
                    </PrimaryBtn>
                </TextBox>
            </Flex>
            <Flex>
                <BottleWrapper>
                    <StaticImage
                        src="../../../assets/images/home/supercharge/bottle.png"
                        alt="copper plus bottle, smart az minerals"
                        width={460}
                        height={460}
                    />
                    <div>
                        <p>Copper Plus</p>
                        <p>$91.00</p>
                    </div>
                </BottleWrapper>

                <TextBox>
                    <p className="tagline">Antioxidant with the potential to better the skin.</p>
                    <h2>Start Looking Better!</h2>

                    <SecondaryBtn as={CLink} to={getNavLink('shop all')}>
                        See All Products
                    </SecondaryBtn>
                </TextBox>
            </Flex>
        </Section>
    );
};
